import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Star, Phone, ArrowLeft, ShieldCheck, Share2, Copy, Check, Mail, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/Layout";
import Breadcrumbs from "../components/Breadcrumbs";
import PreFooterCTA from "../components/PreFooterCTA";
import ReviewSurvey from "../components/ReviewSurvey";
import { BUSINESS, SITE_URL } from "../data/business";

const BREADCRUMBS = [{ label: "Reviews", to: "/reviews" }, { label: "Write a Review" }];

const SHARE_URL = `${SITE_URL}/write-a-review`;
const SHARE_TEXT = "How did we do? Leave Drain Sights a quick review:";

function ShareBar() {
  const [copied, setCopied] = useState(false);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(SHARE_URL);
      setCopied(true);
      toast.success("Review link copied", { description: SHARE_URL });
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error("Couldn't copy — long-press the link to copy it manually.");
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "Review Drain Sights", text: SHARE_TEXT, url: SHARE_URL });
      } catch (e) {
        /* user dismissed the share sheet — no action needed */
      }
    } else {
      copyLink();
    }
  };

  const smsHref = `sms:?&body=${encodeURIComponent(`${SHARE_TEXT} ${SHARE_URL}`)}`;
  const emailHref = `mailto:?subject=${encodeURIComponent("Leave Drain Sights a review")}&body=${encodeURIComponent(`${SHARE_TEXT}\n\n${SHARE_URL}`)}`;

  return (
    <div className="abc-card p-5 sm:p-6 text-left" data-testid="review-share-bar">
      <div className="flex items-center gap-2 text-[#1A1D21] font-display font-bold">
        <Share2 className="w-4 h-4 text-[#E31D2B]" /> Share this review link
      </div>
      <p className="mt-1 text-sm text-slate-500">
        Send this page to a customer so they can leave a review in seconds.
      </p>

      <div className="mt-4 flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 p-1.5">
        <span className="flex-1 truncate text-sm text-slate-600 px-2" data-testid="review-share-url">{SHARE_URL}</span>
        <button
          type="button"
          onClick={copyLink}
          className="abc-btn abc-btn-accent !py-2 !px-3 shrink-0"
          data-testid="review-copy-link"
          aria-label="Copy review link"
        >
          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" onClick={nativeShare} className="abc-btn abc-btn-outline !py-2" data-testid="review-share-native">
          <Share2 className="w-4 h-4" /> Share
        </button>
        <a href={smsHref} className="abc-btn abc-btn-outline !py-2" data-testid="review-share-sms">
          <MessageSquare className="w-4 h-4" /> Text
        </a>
        <a href={emailHref} className="abc-btn abc-btn-outline !py-2" data-testid="review-share-email">
          <Mail className="w-4 h-4" /> Email
        </a>
      </div>
    </div>
  );
}

export default function WriteReview() {
  return (
    <Layout>
      <section className="py-14 md:py-20 abc-bg-pale">
        <div className="abc-container max-w-3xl">
          <div className="text-center">
            <div className="flex justify-center"><Breadcrumbs items={BREADCRUMBS} /></div>

            <div className="mt-6 inline-flex items-center gap-1.5 text-[#E31D2B] text-[11px] font-bold uppercase tracking-[0.16em]">
              <ShieldCheck className="w-3.5 h-3.5" /> Thanks for considering us
            </div>

            <div className="mt-4 flex items-center justify-center gap-0.5 text-[#E31D2B]">
              {["s1", "s2", "s3", "s4", "s5"].map((k) => (
                <Star key={k} className="w-7 h-7 fill-[#E31D2B]" />
              ))}
            </div>

            <h1 className="font-display mt-5 text-3xl sm:text-4xl font-extrabold text-[#1A1D21] leading-tight">
              Share your experience with <span className="text-[#E31D2B]">Drain Sights</span>
            </h1>

            <p className="mt-4 text-slate-600 text-[15px] leading-relaxed max-w-xl mx-auto">
              We're a small local drain &amp; sewer team and your feedback genuinely helps us. If we got it right, we'd be honored if you'd take a minute to tell us how the work went.
            </p>
          </div>

          {/* Embedded GHL review survey */}
          <div className="mt-8" data-testid="review-survey-wrap">
            <ReviewSurvey />
          </div>

          {/* Easy sharing for the team + customers */}
          <div className="mt-8">
            <ShareBar />
          </div>

          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <a href={BUSINESS.phoneHref} className="abc-btn abc-btn-accent" data-testid="write-review-call">
              <Phone className="w-4 h-4" /> Call {BUSINESS.phone}
            </a>
            <Link to="/reviews" className="abc-btn abc-btn-outline" data-testid="write-review-back">
              <ArrowLeft className="w-4 h-4" /> Back to reviews
            </Link>
          </div>
        </div>
      </section>

      <PreFooterCTA />
    </Layout>
  );
}
